import { ComponentProps, FunctionComponent, useEffect } from 'react';
import { Navigate, Outlet, useLoaderData, useLocation, useParams } from 'react-router';
import { NotFoundError } from '../../common/error/NotFoundError';
import { LoaderWithConfig, primeSwrCache } from '../../common/utils/loaderFactory';
import { ConversationProvider } from '../messages/ConversationContext';
import { mapToFunctionPermissions } from './models/FunctionPermissions';
import { GetPropertyAssociationConfigResponse } from './models/GetPropertyAssociationConfigResponse';
import { PropertyAssociationInfo } from './models/PropertyAssociationInfo';
import { PropertyAssociationProvider, usePropertyAssociationContext } from './PropertyAssociationContext';
import { NO_AREA_PLACEHOLDER } from './propertyAssociationEnums';
import { getPropertyAssociationAsync, getPropertyAssociationConfigAsync, usePropertyAssociation, usePropertyAssociationConfig } from './propertyAssociationService';
import { useLastAreaId } from './useLastAreaId';

type LoaderData = {
    config: GetPropertyAssociationConfigResponse;
    info: PropertyAssociationInfo;
};

export const propertyAssociationLoader: LoaderWithConfig<LoaderData> = async ({ params }, config) => {
    try {
        const associationPromise = getPropertyAssociationAsync(config.apiBaseUrl, params.propertyAssociationId!);
        const configPromise = getPropertyAssociationConfigAsync(config.apiBaseUrl, params.propertyAssociationId!);
        const [info, configData] = await Promise.all([associationPromise, configPromise]);

        // Prime SWR cache with loader data
        primeSwrCache(`${config.apiBaseUrl}/propertyassociations/${params.propertyAssociationId}`, info);
        primeSwrCache(`${config.apiBaseUrl}/propertyassociations/${params.propertyAssociationId}/config`, configData);

        return { info, config: configData };
    } catch (error) {
        if (error instanceof Error && error.name === 'NotFoundError') {
            throw new NotFoundError('Fastighetsföreningen kunde inte hittas. Kontrollera att länken är korrekt.');
        }
        throw error;
    }
};

export const PropertyAssociationLayout: FunctionComponent = () => {
    const parentContext = usePropertyAssociationContext();

    const { propertyAssociationId, areaId } = useParams();
    const location = useLocation();
    const { config, info } = useLoaderData<LoaderData>();

    // Get area ID from navigation state (for non-area pages when user changes area)
    const selectedAreaIdFromState = (location.state as { selectedAreaId?: string } | null)?.selectedAreaId;
    const { propertyAssociation } = usePropertyAssociation(propertyAssociationId!, info);
    const { propertyAssociationConfig } = usePropertyAssociationConfig(propertyAssociationId!, config);

    // Loader guarantees these exist
    const { areas, functionPermissions, propertyOwner } = propertyAssociationConfig!;

    // Find the current area from URL param
    // - If areaId is the placeholder '_', there are no areas - PA acts as area (currentArea = undefined)
    // - If areaId exists, find matching area
    // - If only one area, auto-select it
    // - If no areaId in URL (non-area page), check sessionStorage for last selected area
    const isNoAreaPlaceholder = areaId === NO_AREA_PLACEHOLDER;

    // Store the last selected areaId in sessionStorage when it changes
    const { getLastAreaId, setLastAreaId } = useLastAreaId(propertyAssociationId);
    useEffect(() => {
        if (areaId && areaId !== NO_AREA_PLACEHOLDER) {
            setLastAreaId(areaId);
        } else if (selectedAreaIdFromState) {
            // Also persist selection from navigation state (for non-area pages)
            setLastAreaId(selectedAreaIdFromState);
        }
    }, [areaId, selectedAreaIdFromState, setLastAreaId]);

    // Redirect if using NO_AREA_PLACEHOLDER but PA actually has areas
    if (isNoAreaPlaceholder && areas.length > 0) {
        return <Navigate to={`/${propertyAssociationId}/areas/`} replace />;
    }

    // Determine current area: from URL, navigation state, sessionStorage (for non-area pages), or auto-select if single area
    const lastAreaId = getLastAreaId();
    const findAreaById = (id?: string | null) => (id ? areas.find((area) => area.propertyAssociationAreaId === id) : undefined);

    let currentArea: (typeof areas)[number] | undefined = undefined;

    if (!isNoAreaPlaceholder) {
        const preferredAreaId = areaId ?? selectedAreaIdFromState ?? lastAreaId;
        currentArea = findAreaById(preferredAreaId) ?? (areas.length === 1 ? areas[0] : undefined);
    }

    // MERGE parent
    const contextValue = {
        // Keep parent's app-level data
        ...parentContext,

        // ADD property-level data
        currentPropertyAssociation: propertyAssociation,
        currentPropertyAssociationId: propertyAssociationId!,
        allAreas: areas,
        currentArea,
        currentFunctionPermissions: mapToFunctionPermissions(functionPermissions),
        currentPropertyOwner: propertyOwner,
        isGuest: propertyAssociation?.isGuest ?? true,
        isRBHyr: propertyOwner?.tag === 'RBHYR',
    } satisfies ComponentProps<typeof PropertyAssociationProvider>['value'];

    return (
        <PropertyAssociationProvider value={contextValue}>
            <ConversationProvider>
                <Outlet />
            </ConversationProvider>
        </PropertyAssociationProvider>
    );
};
